
<template>
  <v-card elevation="2" class="ma-1">
    <v-tabs v-model="tab" align="start" background-color="blue lighten-5">
      <v-tab>Général</v-tab>
      <v-tab>Requêtes</v-tab>
      <v-tab>Étapes et champs</v-tab>

      <v-tabs-items v-model="tab" class="pa-4">
        <v-tab-item>
          <v-row>
            <v-col cols="12">
              <h3 class="mb-4">Présentation du formulaire</h3>

              <TextField
                :item="{
                  name: 'Titre du formulaire',
                  value: 'title',
                  defaultprops: { show: true, clearable: true }
                }"
                :valField="displays.params.general.title"
                :keyup="(item, value) => { displays.params.general.title = value; }"
              />

              <v-textarea
                v-model="displays.params.general.description"
                label="Description du formulaire"
                hint="Texte affiché au public avant les champs du formulaire."
                persistent-hint
                outlined
                auto-grow
                rows="3"
              />
            </v-col>

            <v-col cols="12" md="6">
              <h3 class="mb-4">Navigation</h3>

              <SwitchC
                :item="{ name: 'Activer le parcours en plusieurs étapes', value: 'wizard', defaultprops: { show: true } }"
                :valField="displays.params.wizard.enabled"
                :change="(item, value) => { displays.params.wizard.enabled = value; }"
              />

              <TextField
                :item="{ name: 'Libellé du bouton précédent', value: 'previousLabel', defaultprops: { show: true } }"
                :valField="displays.params.general.previousLabel"
                :keyup="(item, value) => { displays.params.general.previousLabel = value; }"
              />

              <TextField
                :item="{ name: 'Libellé du bouton suivant', value: 'nextLabel', defaultprops: { show: true } }"
                :valField="displays.params.general.nextLabel"
                :keyup="(item, value) => { displays.params.general.nextLabel = value; }"
              />

              <TextField
                :item="{ name: 'Libellé du bouton de validation', value: 'submitLabel', defaultprops: { show: true } }"
                :valField="displays.params.general.submitLabel"
                :keyup="(item, value) => { displays.params.general.submitLabel = value; }"
              />

              <SwitchC
                :item="{ name: 'Afficher le récapitulatif avant validation', value: 'summary', defaultprops: { show: true } }"
                :valField="displays.params.general.summary.enabled"
                :change="(item, value) => { displays.params.general.summary.enabled = value; }"
              />

              <TextField
                v-if="displays.params.general.summary.enabled"
                :item="{ name: 'Titre du récapitulatif', value: 'summaryTitle', defaultprops: { show: true } }"
                :valField="displays.params.general.summary.title"
                :keyup="(item, value) => { displays.params.general.summary.title = value; }"
              />

              <SwitchC
                :item="{
                  name: 'Recalculer automatiquement les montants après modification d’une sélection',
                  value: 'autoRecalculateAmounts',
                  defaultprops: { show: true }
                }"
                :valField="displays.params.general.autoRecalculateAmounts"
                :change="(item, value) => { displays.params.general.autoRecalculateAmounts = value; }"
              />
            </v-col>

            <v-col cols="12" md="6">
              <h3 class="mb-4">Messages affichés au public</h3>

              <TextField
                :item="{ name: 'Titre après validation', value: 'successTitle', defaultprops: { show: true } }"
                :valField="displays.params.general.successTitle"
                :keyup="(item, value) => { displays.params.general.successTitle = value; }"
              />

              <v-textarea
                v-model="displays.params.general.successMessage"
                label="Message après validation"
                outlined
                auto-grow
                rows="3"
              />

              <SwitchC
                :item="{ name: 'Afficher le bouton de nouvelle saisie', value: 'showResetButton', defaultprops: { show: true } }"
                :valField="displays.params.general.showResetButton"
                :change="(item, value) => { displays.params.general.showResetButton = value; }"
              />

              <TextField
                v-if="displays.params.general.showResetButton"
                :item="{ name: 'Libellé du bouton de nouvelle saisie', value: 'resetButtonLabel', defaultprops: { show: true } }"
                :valField="displays.params.general.resetButtonLabel"
                :keyup="(item, value) => { displays.params.general.resetButtonLabel = value; }"
              />

              <v-textarea
                v-model="displays.params.general.duplicateMessage"
                label="Message en cas de doublon"
                hint="La variable [[queryLabel]] sera remplacée par le libellé de la requête concernée."
                persistent-hint
                outlined
                auto-grow
                rows="3"
              />
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item>
          <div class="d-flex align-center mb-4">
            <div>
              <h3>Liste des requêtes</h3>
              <div class="text-body-2 text--secondary">
                Les champs sélectionnés seront récupérés par la requête lors de l'affichage du formulaire.
              </div>
            </div>
            <v-spacer />
            <v-btn color="primary" @click="addQuery">
              <v-icon left>mdi-plus</v-icon>
              Ajouter une requête
            </v-btn>
          </div>

          <v-alert v-if="!displays.params.querys.length" type="info" outlined>
            Aucune requête n'est encore configurée.
          </v-alert>

          <v-expansion-panels v-model="openedQueries" multiple popout>
            <v-expansion-panel
              v-for="(query, index) in displays.params.querys"
              :key="`publicform-query-${index}`"
            >
              <v-expansion-panel-header>
                <div class="d-flex align-center flex-grow-1">
                  <div>
                    <strong>{{ query.title || 'Requête sans titre' }}</strong>
                    <div class="text-caption text--secondary">
                      Clé : {{ query.key || 'non renseignée' }}
                    </div>
                  </div>
                  <v-spacer />
                  <v-btn icon color="error" @click.stop="removeQuery(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="grey lighten-4">
                <v-row>
                  <v-col cols="12" md="6">
                    <TextField
                      :item="{ name: 'Titre de la requête', value: 'title', defaultprops: { show: true } }"
                      :valField="query.title"
                      :keyup="(item, value) => { query.title = value; }"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <TextField
                      :item="{ name: 'Clé de la requête', value: 'key', defaultprops: { show: true } }"
                      :valField="query.key"
                      :keyup="(item, value) => { query.key = value; }"
                    />
                  </v-col>

                  <v-col cols="12">
                    <Select
                      :item="{
                        name: 'Table ciblée',
                        value: 'table',
                        liste: itemTableListe,
                        defaultprops: { show: true, clearable: true }
                      }"
                      :valField="query.table"
                      :change="(item, value) => { query.table = value?.value || ''; }"
                    />
                  </v-col>

                  <v-col cols="12">
                    <SwitchC
                      :item="{
                        name: 'Bloquer l’envoi si cette requête trouve un doublon',
                        value: 'stopifdoublon',
                        defaultprops: { show: true }
                      }"
                      :valField="query.stopifdoublon"
                      :change="(item, value) => { query.stopifdoublon = value; }"
                    />
                  </v-col>
                </v-row>

                <v-tabs v-if="query.table" v-model="queryTabs[index]" background-color="transparent">
                  <v-tab>Filtre</v-tab>
                  <v-tab>Champs à récupérer</v-tab>

                  <v-tabs-items v-model="queryTabs[index]">
                    <v-tab-item class="pa-3">
                      <Filtre
                        :page="page"
                        :filtre="query.filtre"
                        :table="query.table"
                      />
                    </v-tab-item>

                    <v-tab-item class="pa-3">
                      <Fields
                        :page="page"
                        :champs="query.champs"
                        :modalKey="modalKey"
                        :filter="query.filtre"
                        :table="query.table"
                      />
                    </v-tab-item>
                  </v-tabs-items>
                </v-tabs>

                <v-alert v-else type="info" text dense class="mt-3 mb-0">
                  Sélectionnez une table pour configurer le filtre et les champs à récupérer.
                </v-alert>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-tab-item>

        <v-tab-item>
          <v-tabs v-model="fieldsTab" background-color="transparent">
            <v-tab>Étapes</v-tab>
            <v-tab>Champs fixes</v-tab>
          </v-tabs>

          <v-tabs-items v-model="fieldsTab">
            <v-tab-item class="pt-4">
          <div class="d-flex align-center mb-4">
            <div>
              <h3>Construction du formulaire</h3>
              <div class="text-body-2 text--secondary">
                Les étapes et leur contenu peuvent être réorganisés par glisser-déposer.
              </div>
            </div>
            <v-spacer />
            <v-btn color="primary" @click="addStep">
              <v-icon left>mdi-plus</v-icon>
              Ajouter une étape
            </v-btn>
          </div>

          <draggable
            v-model="wizard.steps"
            v-bind="dragOptions"
            handle=".step-handle"
            tag="v-expansion-panels"
            :component-data="{ props: { multiple: true } }"
            class="publicform-steps"
            @change="syncFields"
          >
            <v-expansion-panel
              v-for="(step, stepIndex) in wizard.steps"
              :key="`step-${stepIndex}`"
            >
              <v-expansion-panel-header class="blue lighten-5">
                <div class="d-flex align-center flex-grow-1">
                  <v-icon class="step-handle mr-3">mdi-drag</v-icon>
                  <div>
                    <strong>{{ step.title || `Étape ${stepIndex + 1}` }}</strong>
                    <div class="text-caption text--secondary">
                      {{ step.items.length }} élément{{ step.items.length > 1 ? 's' : '' }}
                    </div>
                  </div>
                  <v-spacer />
                  <v-btn
                    icon
                    color="error"
                    :disabled="wizard.steps.length === 1"
                    @click.stop="removeStep(stepIndex)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="pt-4">
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="step.key" label="Clé de l'étape" outlined dense />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="step.title" label="Titre de l'étape" outlined dense />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="step.description" label="Description" outlined dense />
                  </v-col>
                </v-row>

                <div class="d-flex flex-wrap mb-4 publicform-add-buttons">
                  <v-btn small outlined color="info" @click="addItem(stepIndex, 'content')">
                    <v-icon left>mdi-text-box-outline</v-icon>Texte
                  </v-btn>
                  <v-btn small outlined color="primary" @click="addItem(stepIndex, 'field')">
                    <v-icon left>mdi-form-textbox</v-icon>Champ de la table
                  </v-btn>
                  <v-btn small outlined color="secondary" @click="addItem(stepIndex, 'virtual')">
                    <v-icon left>mdi-tune-variant</v-icon>Champ virtuel
                  </v-btn>
                  <v-btn small outlined color="teal" @click="addItem(stepIndex, 'queryList')">
                    <v-icon left>mdi-format-list-checks</v-icon>Liste issue d'une requête
                  </v-btn>
                  <v-btn small outlined color="deep-purple" @click="addItem(stepIndex, 'promoCode')">
                    <v-icon left>mdi-ticket-percent-outline</v-icon>Code promo
                  </v-btn>
                </div>

                <draggable
                  :list="step.items"
                  v-bind="dragOptions"
                  group="publicform-items"
                  handle=".item-handle"
                  tag="v-expansion-panels"
                  :component-data="{ props: { multiple: true } }"
                  class="publicform-items"
                  @change="syncFields"
                >
                    <v-expansion-panel
                      v-for="(item, itemIndex) in step.items"
                      :key="`${stepIndex}-${itemIndex}`"
                    >
                      <v-expansion-panel-header>
                        <div class="d-flex align-center flex-grow-1">
                          <v-icon class="item-handle mr-3">mdi-drag</v-icon>
                          <div>
                            <strong>{{ itemTitle(item) }}</strong>
                            <div class="text-caption text--secondary">
                              ID condition : <strong>{{ item.blockId }}</strong> · {{ itemTypeLabel(item.kind) }} · largeur {{ item.layout.width }}
                            </div>
                          </div>
                          <v-spacer />
                          <v-btn icon color="error" @click.stop="removeItem(stepIndex, itemIndex)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </v-expansion-panel-header>

                      <v-expansion-panel-content class="grey lighten-4">
                        <v-row>
                          <v-col cols="12" md="4">
                            <v-select
                              v-model="item.layout.width"
                              :items="widthOptions"
                              label="Largeur"
                              outlined
                              dense
                            />
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-select
                              v-model="item.layout.position"
                              :items="positionOptions"
                              label="Position dans la ligne"
                              outlined
                              dense
                            />
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-select
                              v-model="item.layout.textAlign"
                              :items="textAlignOptions"
                              label="Alignement du contenu"
                              outlined
                              dense
                            />
                          </v-col>
                        </v-row>

                        <template v-if="item.kind === 'content'">
                          <v-row>
                            <v-col cols="12" md="4">
                              <v-select
                                v-model="item.contentType"
                                :items="contentTypeOptions"
                                label="Type de contenu"
                                outlined
                                dense
                              />
                            </v-col>
                            <v-col cols="12" md="8">
                              <v-text-field v-model="item.title" label="Titre" outlined dense />
                            </v-col>
                            <v-col cols="12">
                              <v-textarea v-model="item.content" label="Texte" outlined auto-grow rows="3" />
                            </v-col>
                          </v-row>
                        </template>

                        <template v-else-if="item.kind === 'field'">
                          <v-row>
                            <v-col cols="12" md="6">
                              <v-select
                                v-model="item.field"
                                :items="fieldOptions"
                                label="Champ de la table"
                                outlined
                                dense
                                clearable
                                @change="onTableFieldChange(item)"
                              />
                            </v-col>
                            <v-col cols="12" md="6">
                              <v-text-field v-model="item.title" label="Titre affiché" outlined dense />
                            </v-col>
                            <v-col cols="12">
                              <v-textarea v-model="item.description" label="Aide ou description" outlined auto-grow rows="2" />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-select
                                v-model="item.usage"
                                :items="fieldUsageOptions"
                                label="Utilisation de la valeur"
                                outlined
                                dense
                                @change="syncFields"
                              />
                            </v-col>
                            <v-col v-if="fieldHasOptions(item.field)" cols="12" md="4">
                              <v-select
                                v-model="item.renderAs"
                                :items="listRenderOptions"
                                label="Affichage de la liste"
                                outlined
                                dense
                              />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-switch v-model="item.required" label="Champ obligatoire" color="primary" />
                            </v-col>
                          </v-row>
                        </template>

                        <template v-else-if="item.kind === 'virtual'">
                          <v-row>
                            <v-col cols="12" md="6">
                              <v-text-field v-model="item.key" label="Clé du champ virtuel" outlined dense />
                            </v-col>
                            <v-col cols="12" md="6">
                              <v-text-field v-model="item.title" label="Titre affiché" outlined dense />
                            </v-col>
                            <v-col cols="12">
                              <v-textarea v-model="item.description" label="Aide ou description" outlined auto-grow rows="2" />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-select
                                v-model="item.inputType"
                                :items="virtualTypeOptions"
                                label="Type de champ"
                                outlined
                                dense
                              />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-text-field v-model="item.defaultValue" label="Valeur par défaut" outlined dense />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-switch v-model="item.required" label="Champ obligatoire" color="primary" />
                            </v-col>
                            <v-col v-if="virtualUsesOptions(item)" cols="12">
                              <ArrayTable
                                :item="virtualOptionsTable"
                                :valField="item.options"
                                :change="(field, value) => updateVirtualOptions(item, value)"
                              />
                            </v-col>
                          </v-row>
                        </template>

                        <template v-else-if="item.kind === 'queryList'">
                          <v-row>
                            <v-col cols="12" md="4">
                              <v-text-field v-model="item.key" label="Clé de sortie" outlined dense />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-text-field v-model="item.title" label="Titre affiché" outlined dense />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-select
                                v-model="item.queryKey"
                                :items="queryOptions"
                                label="Requête source"
                                outlined
                                dense
                                clearable
                              />
                            </v-col>
                            <v-col cols="12">
                              <v-textarea v-model="item.description" label="Aide ou description" outlined auto-grow rows="2" />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-select
                                v-model="item.selectionMode"
                                :items="selectionModeOptions"
                                label="Mode de sélection"
                                outlined
                                dense
                              />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-select
                                v-model="item.renderAs"
                                :items="queryRenderOptions"
                                label="Affichage"
                                outlined
                                dense
                              />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-select
                                v-model="item.usage"
                                :items="fieldUsageOptions"
                                label="Utilisation de la sélection"
                                outlined
                                dense
                                @change="syncFields"
                              />
                            </v-col>
                            <v-col cols="12" md="2">
                              <v-switch v-model="item.required" label="Obligatoire" color="primary" />
                            </v-col>
                            <v-col cols="12" md="2">
                              <v-switch v-model="item.includeInSummary" label="Récapitulatif" color="primary" />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-switch
                                v-model="item.includeInAmountCalculation"
                                label="Inclure dans le calcul des montants"
                                color="primary"
                              />
                            </v-col>
                          </v-row>

                          <h4 class="mb-2">Correspondance des champs retournés par la requête</h4>
                          <v-row>
                            <v-col cols="12" md="4">
                              <v-text-field v-model="item.mapping.value" label="Identifiant / valeur" outlined dense />
                            </v-col>
                            <v-col cols="12" md="8">
                              <v-text-field v-model="item.mapping.label" label="Libellé" outlined dense />
                            </v-col>
                            <v-col cols="12" md="3">
                              <v-text-field v-model="item.mapping.amountHt" label="Montant HT" outlined dense />
                            </v-col>
                            <v-col cols="12" md="3">
                              <v-text-field v-model="item.mapping.vatRate" label="Taux de TVA" outlined dense />
                            </v-col>
                            <v-col cols="12" md="3">
                              <v-text-field v-model="item.mapping.vatAmount" label="Montant TVA" outlined dense />
                            </v-col>
                            <v-col cols="12" md="3">
                              <v-text-field v-model="item.mapping.amountTtc" label="Montant TTC" outlined dense />
                            </v-col>
                            <v-col cols="12" md="3">
                              <v-text-field
                                v-model="item.mapping.promoApplicable"
                                label="Promo applicable"
                                hint="Champ retournant 1 si la prestation accepte les promotions, 0 sinon."
                                persistent-hint
                                outlined
                                dense
                              />
                            </v-col>
                          </v-row>

                          <h4 class="mb-2">Format transmis lors de l'enregistrement</h4>
                          <v-row>
                            <v-col cols="12" md="6">
                              <v-select
                                v-model="item.return.format"
                                :items="queryReturnFormatOptions"
                                label="Format de retour"
                                outlined
                                dense
                              />
                            </v-col>
                            <v-col v-if="item.return.format !== 'array_objects'" cols="12" md="6">
                              <v-select
                                v-model="item.return.cast"
                                :items="returnCastOptions"
                                label="Type des valeurs"
                                outlined
                                dense
                              />
                            </v-col>
                            <v-col v-else cols="12">
                              <ArrayTable
                                :item="queryObjectFieldsTable"
                                :valField="item.return.fields"
                                :change="(field, value) => updateQueryReturnFields(item, value)"
                              />
                            </v-col>
                          </v-row>

                          <h4 class="mb-2">Gestion de la quantité</h4>
                          <v-row>
                            <v-col cols="12" md="6">
                              <v-select
                                v-model="item.quantity.mode"
                                :items="quantityModeOptions"
                                label="Mode de quantité"
                                outlined
                                dense
                              />
                            </v-col>
                            <v-col v-if="item.quantity.mode === 'fixed'" cols="12" md="6">
                              <v-text-field
                                v-model.number="item.quantity.fixedValue"
                                type="number"
                                min="0.000001"
                                step="any"
                                label="Quantité fixe"
                                outlined
                                dense
                              />
                            </v-col>
                            <template v-else>
                              <v-col cols="12" md="6">
                                <v-text-field v-model="item.quantity.label" label="Libellé" outlined dense />
                              </v-col>
                              <v-col cols="12" md="3">
                                <v-text-field v-model.number="item.quantity.default" type="number" min="0.000001" step="any" label="Quantité par défaut" outlined dense />
                              </v-col>
                              <v-col cols="12" md="3">
                                <v-text-field v-model.number="item.quantity.min" type="number" min="0" step="any" label="Quantité minimale" outlined dense />
                              </v-col>
                              <v-col cols="12" md="3">
                                <v-text-field v-model.number="item.quantity.max" type="number" min="0" step="any" label="Quantité maximale" clearable outlined dense />
                              </v-col>
                              <v-col cols="12" md="3">
                                <v-text-field v-model.number="item.quantity.step" type="number" min="0.000001" step="any" label="Pas" outlined dense />
                              </v-col>
                            </template>
                            <v-col cols="12">
                              <v-alert type="info" text dense class="mb-0">
                                Pour récupérer la quantité dans un retour <code>array_objects</code>, ajoutez une propriété dont la source est <code>[[quantity]]</code>.
                              </v-alert>
                            </v-col>
                          </v-row>
                        </template>

                        <template v-else-if="item.kind === 'promoCode'">
                          <v-row>
                            <v-col cols="12" md="4">
                              <v-text-field
                                v-model="item.key"
                                label="Clé d’enregistrement (facultative)"
                                hint="Si elle est renseignée, le code promo validé sera enregistré dans ce champ."
                                persistent-hint
                                outlined
                                dense
                                @change="syncFields"
                              />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-text-field v-model="item.title" label="Titre affiché" outlined dense />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-select
                                v-model="item.queryKey"
                                :items="queryOptions"
                                label="Requête source des codes promo"
                                outlined
                                dense
                                clearable
                              />
                            </v-col>
                            <v-col cols="12">
                              <v-textarea v-model="item.description" label="Aide ou description" outlined auto-grow rows="2" />
                            </v-col>
                            <v-col cols="12" md="6">
                              <v-text-field v-model="item.placeholder" label="Texte indicatif du champ" outlined dense />
                            </v-col>
                            <v-col cols="12" md="6">
                              <v-text-field v-model="item.buttonLabel" label="Libellé du bouton" outlined dense />
                            </v-col>
                          </v-row>

                          <h4 class="mb-2">Correspondance des champs retournés par la requête</h4>
                          <v-row>
                            <v-col cols="12" md="4">
                              <v-text-field v-model="item.mapping.code" label="Code promo" outlined dense />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-text-field v-model="item.mapping.discountValue" label="Valeur de la remise" outlined dense />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-text-field v-model="item.mapping.discountType" label="Type de remise" outlined dense />
                            </v-col>
                          </v-row>

                          <h4 class="mb-2">Valeurs des types de remise</h4>
                          <v-row>
                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="item.typeValues.percentage"
                                label="Valeur représentant un pourcentage"
                                outlined
                                dense
                              />
                            </v-col>
                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="item.typeValues.fixed"
                                label="Valeur représentant une remise fixe"
                                outlined
                                dense
                              />
                            </v-col>
                          </v-row>

                          <v-alert type="info" text dense class="mb-0">
                            Les résultats de cette requête ne sont pas envoyés au formulaire public. Ils seront exploités côté newapp par le futur moteur de calcul.
                          </v-alert>
                        </template>

                        <v-expansion-panels accordion flat class="mt-3">
                          <v-expansion-panel>
                            <v-expansion-panel-header>Conditions d'affichage et de saisie</v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <v-alert type="info" text dense>
                                Dans la valeur de la variable d'une condition, indiquez uniquement l'ID condition du bloc concerné (par exemple <code>{{ item.blockId }}</code>), et non sa clé métier.
                              </v-alert>
                              <h4 class="mb-2">Afficher si</h4>
                              <groupConditions
                                :conditions="item.conditions.visibleIf"
                                @recepVal="(value) => updateCondition(item, 'visibleIf', value)"
                              />
                              <template v-if="item.kind !== 'content'">
                                <h4 class="mt-4 mb-2">Rendre obligatoire si</h4>
                                <groupConditions
                                  :conditions="item.conditions.requiredIf"
                                  @recepVal="(value) => updateCondition(item, 'requiredIf', value)"
                                />
                                <h4 class="mt-4 mb-2">Désactiver si</h4>
                                <groupConditions
                                  :conditions="item.conditions.disabledIf"
                                  @recepVal="(value) => updateCondition(item, 'disabledIf', value)"
                                />
                              </template>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                </draggable>

                <v-alert v-if="!step.items.length" type="info" text dense class="mb-0">
                  Cette étape ne contient encore aucun élément.
                </v-alert>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </draggable>
            </v-tab-item>

            <v-tab-item class="pt-4">
              <h3>Champs à valeur fixe</h3>
              <v-alert type="info" outlined class="mt-3">
                Ces valeurs seront ajoutées à l'inscription par le module. Elles ne seront pas demandées à l'utilisateur.
              </v-alert>

              <ArrayTable
                :item="fixedFieldsTable"
                :valField="displays.params.defaults"
                :change="updateFixedFields"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
  </v-card>
</template>

<script>
import { admin } from '../mixins';
import draggable from 'vuedraggable';

export default {
  name: "PublicformModule",

  mixins: [admin],

  components: {
    draggable
  },

  data: function () {
    return {
      tab: 0,
      fieldsTab: 0,
      displays: this.module.displays,
      openedQueries: [],
      queryTabs: [],
      allFields: [],
      fieldOptions: [],
      widthOptions: [
        { text: '1/6', value: '1/6' },
        { text: '2/6', value: '2/6' },
        { text: '3/6', value: '3/6' },
        { text: '4/6', value: '4/6' },
        { text: '5/6', value: '5/6' },
        { text: '6/6', value: '6/6' }
      ],
      positionOptions: [
        { text: 'À gauche', value: 'left' },
        { text: 'Centré', value: 'center' },
        { text: 'À droite', value: 'right' }
      ],
      textAlignOptions: [
        { text: 'À gauche', value: 'left' },
        { text: 'Centré', value: 'center' },
        { text: 'À droite', value: 'right' }
      ],
      contentTypeOptions: [
        { text: 'Texte simple', value: 'text' },
        { text: 'Information', value: 'info' },
        { text: 'Succès', value: 'success' },
        { text: 'Alerte', value: 'warning' },
        { text: 'Erreur', value: 'error' },
        { text: 'Autre', value: 'other' }
      ],
      fieldUsageOptions: [
        { text: 'Insérer la valeur dans la table', value: 'insert' },
        { text: 'Utiliser uniquement dans le visuel du formulaire', value: 'visual' }
      ],
      listRenderOptions: [
        { text: 'Liste déroulante', value: 'select' },
        { text: 'Boutons radio', value: 'radio' },
        { text: 'Cases à cocher', value: 'checkboxes' }
      ],
      virtualTypeOptions: [
        { text: 'Texte', value: 'text' },
        { text: 'Zone de texte', value: 'textarea' },
        { text: 'Liste déroulante', value: 'select' },
        { text: 'Boutons radio', value: 'radio' },
        { text: 'Cases à cocher', value: 'checkboxes' },
        { text: 'Choix ON / OFF', value: 'switch' }
      ],
      selectionModeOptions: [
        { text: 'Choix unique', value: 'single' },
        { text: 'Choix multiples', value: 'multiple' }
      ],
      queryRenderOptions: [
        { text: 'Cartes', value: 'cards' },
        { text: 'Liste déroulante', value: 'select' },
        { text: 'Boutons radio', value: 'radio' },
        { text: 'Cases à cocher', value: 'checkboxes' }
      ],
      queryReturnFormatOptions: [
        { text: 'Valeur simple', value: 'scalar' },
        { text: 'Tableau de valeurs', value: 'array' },
        { text: "Tableau d'objets", value: 'array_objects' }
      ],
      quantityModeOptions: [
        { text: 'Quantité fixe', value: 'fixed' },
        { text: "Quantité saisie par l'utilisateur", value: 'input' }
      ],
      returnCastOptions: [
        { text: 'Texte', value: 'string' },
        { text: 'Nombre entier', value: 'integer' },
        { text: 'Nombre décimal', value: 'float' },
        { text: 'Booléen', value: 'boolean' }
      ]
    };
  },

  computed: {
    wizard() {
      return this.displays.params.wizard;
    },

    dragOptions() {
      return {
        animation: 150,
        ghostClass: 'publicform-ghost'
      };
    },

    queryOptions() {
      return this.displays.params.querys.map((query) => ({
        text: query.title || query.key || 'Requête sans titre',
        value: query.key
      }));
    },

    virtualOptionsTable() {
      return {
        otherparams: {
          arrayTable: [
            { type: '', key: 'text', name: 'Texte affiché' },
            { type: '', key: 'value', name: 'Valeur' }
          ]
        },
        defaultprops: {
          show: true,
          tableDraggable: true,
          tableDel: true,
          hideFooter: true,
          hidesearch: true,
          addTitle: 'Ajouter un choix'
        }
      };
    },

    fixedFieldsTable() {
      return {
        otherparams: {
          arrayTable: [
            {
              type: 'select',
              key: 'key',
              name: 'Champ',
              ArrayValue: this.fieldOptions,
              defaultprops: { show: true, clearable: true }
            },
            {
              type: '',
              key: 'value',
              name: 'Valeur fixe'
            }
          ]
        },
        defaultprops: {
          show: true,
          tableDraggable: false,
          tableDel: true,
          hideFooter: true,
          hidesearch: true,
          addTitle: 'Ajouter une valeur fixe'
        }
      };
    },

    queryObjectFieldsTable() {
      return {
        otherparams: {
          arrayTable: [
            { type: '', key: 'key', name: "Clé dans l'objet" },
            { type: '', key: 'source', name: 'Champ source de la requête' },
            {
              type: 'select',
              key: 'cast',
              name: 'Type',
              ArrayValue: this.returnCastOptions,
              defaultprops: { show: true, clearable: false }
            }
          ]
        },
        defaultprops: {
          show: true,
          tableDraggable: true,
          tableDel: true,
          hideFooter: true,
          hidesearch: true,
          addTitle: 'Ajouter une propriété'
        }
      };
    }
  },

  methods: {
    addQuery() {
      const template = this.buildingParameters.publicform.defaultquerys;
      this.displays.params.querys.push(JSON.parse(JSON.stringify(template)));
      this.openedQueries.push(this.displays.params.querys.length - 1);
    },

    removeQuery(index) {
      this.displays.params.querys.splice(index, 1);
      this.openedQueries = [];
      this.queryTabs.splice(index, 1);
    },

    getFields() {
      this.getListField({ filter: { table: this.module.table }, typeformat: 'form' }).then((fields) => {
        this.allFields = Array.isArray(fields) ? fields : [];
        this.fieldOptions = this.allFields.map((field) => ({
          text: `${field.displays?.name || field.field} (${field.cle})`,
          value: field.cle
        }));
      });
    },

    cloneBuildingParameter(key) {
      const template = this.buildingParameters.publicform[key];
      return JSON.parse(JSON.stringify(template));
    },

    createBlockId() {
      const usedIds = new Set();
      (this.displays.params.wizard?.steps || []).forEach((step) => {
        (step.items || []).forEach((item) => {
          if (item.blockId) usedIds.add(item.blockId);
        });
      });

      let blockId = '';
      do {
        if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
          const bytes = new Uint8Array(4);
          window.crypto.getRandomValues(bytes);
          blockId = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        } else {
          blockId = Math.floor(Math.random() * 0x100000000).toString(16).padStart(8, '0');
        }
      } while (usedIds.has(blockId));

      return blockId;
    },

    normalizeBlockId(blockId) {
      const candidate = String(blockId || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8);
      if (candidate && !this.normalizedBlockIds.has(candidate)) {
        this.normalizedBlockIds.add(candidate);
        return candidate;
      }

      let generated = this.createBlockId();
      while (this.normalizedBlockIds.has(generated)) generated = this.createBlockId();
      this.normalizedBlockIds.add(generated);
      return generated;
    },

    emptyConditions() {
      return {
        visibleIf: [],
        requiredIf: [],
        disabledIf: []
      };
    },

    normalizeItem(source) {
      if (typeof source === 'string') {
        source = { kind: 'field', field: source };
      }

      let kind = source.kind || 'content';
      if (kind === 'block') {
        kind = source.blockType === 'prestations' ? 'queryList' : 'content';
      }
      const templateNames = {
        content: 'defaultcontent',
        field: 'defaultfield',
        virtual: 'defaultvirtualfield',
        queryList: 'defaultqueryblock',
        promoCode: 'defaultpromoblock'
      };
      const templateName = templateNames[kind] || 'defaultcontent';
      const normalized = Object.assign(this.cloneBuildingParameter(templateName), source, { kind });
      normalized.blockId = this.normalizeBlockId(source.blockId);

      if (kind === 'content') {
        normalized.content = source.content || source.description || '';
      }
      if (kind === 'field') {
        normalized.field = source.field || source.cle || '';
        normalized.title = source.title || source.label || '';
        normalized.renderAs = source.renderAs || 'auto';
      }
      if (kind === 'virtual') {
        normalized.inputType = source.inputType || source.type || source.renderAs || 'select';
        normalized.title = source.label || source.title || '';
        normalized.defaultValue = Object.prototype.hasOwnProperty.call(source, 'defaultValue')
          ? source.defaultValue
          : (source.default || '');
        normalized.options = Array.isArray(source.options)
          ? source.options
          : (Array.isArray(source.list) ? source.list : []);
      }
      if (kind === 'queryList') {
        normalized.queryKey = source.queryKey || source.sourceQueryKey || '';
        normalized.selectionMode = source.selectionMode === 'unique'
          ? 'single'
          : (source.selectionMode || 'multiple');
        normalized.usage = source.usage || (source.persist === false ? 'visual' : 'insert');
      }
      if (kind === 'promoCode') {
        normalized.title = source.title || 'Code promo';
      }

      const oldSpan = source.layout?.span || source.span;
      const oldSpanWidths = { 3: '1/6', 6: '3/6', 9: '5/6', 12: '6/6' };
      const legacyWidths = {
        '1/5': '1/6',
        '2/5': '2/6',
        '3/5': '3/6',
        '4/5': '4/6',
        '5/5': '5/6',
        '1/1': '6/6'
      };
      const configuredWidth = source.layout?.width;
      normalized.layout = Object.assign(
        { width: '6/6', position: 'left', textAlign: 'left' },
        source.layout || {}
      );
      normalized.layout.width = legacyWidths[configuredWidth]
        || configuredWidth
        || oldSpanWidths[oldSpan]
        || '6/6';
      normalized.conditions = Object.assign(this.emptyConditions(), source.conditions || {});

      if (kind === 'queryList') {
        normalized.mapping = Object.assign(
          this.cloneBuildingParameter('defaultqueryblock').mapping,
          source.mapping || {}
        );
        normalized.return = Object.assign(
          this.cloneBuildingParameter('defaultqueryblock').return,
          source.return || {}
        );
        normalized.quantity = Object.assign(
          this.cloneBuildingParameter('defaultqueryblock').quantity,
          source.quantity || {}
        );
        if (!source.return?.format) {
          normalized.return.format = normalized.selectionMode === 'multiple' ? 'array' : 'scalar';
        }
        normalized.return.fields = Array.isArray(normalized.return.fields)
          ? normalized.return.fields
          : [];
      }
      if (kind === 'promoCode') {
        normalized.mapping = Object.assign(
          this.cloneBuildingParameter('defaultpromoblock').mapping,
          source.mapping || {}
        );
        normalized.typeValues = Object.assign(
          this.cloneBuildingParameter('defaultpromoblock').typeValues,
          source.typeValues || {}
        );
      }

      return normalized;
    },

    normalizeLegacyGeneral() {
      const general = this.displays.params.general;
      const wizard = this.displays.params.wizard || {};
      if (!Object.prototype.hasOwnProperty.call(general, 'showResetButton')) {
        this.$set(general, 'showResetButton', false);
      }
      if (!general.resetButtonLabel) {
        this.$set(general, 'resetButtonLabel', 'Effectuer une nouvelle saisie');
      }
      if (!Object.prototype.hasOwnProperty.call(general, 'autoRecalculateAmounts')) {
        this.$set(general, 'autoRecalculateAmounts', false);
      }
      if (!general.title && wizard.title) general.title = wizard.title;
      if (!general.description && wizard.intro) general.description = wizard.intro;
      if (wizard.submitlabel && general.submitLabel === 'Valider') general.submitLabel = wizard.submitlabel;
      if (wizard.successTitle) general.successTitle = wizard.successTitle;
      if (wizard.successMessage) general.successMessage = wizard.successMessage;
      if (wizard.duplicateMessage) general.duplicateMessage = wizard.duplicateMessage;
      if (wizard.summary) {
        general.summary = Object.assign(general.summary || {}, wizard.summary);
      }
    },

    normalizeQueries() {
      if (!Array.isArray(this.displays.params.querys)) {
        this.$set(this.displays.params, 'querys', []);
      }
      this.displays.params.querys.forEach((query) => {
        if (!query.title) {
          this.$set(query, 'title', query.description || query.key || 'Nouvelle requête');
        }
      });
    },

    normalizeSteps() {
      this.normalizedBlockIds = new Set();
      if (!this.displays.params.wizard) {
        this.$set(this.displays.params, 'wizard', { enabled: true, steps: [] });
      }

      let steps = Array.isArray(this.displays.params.wizard.steps)
        ? this.displays.params.wizard.steps
        : [];

      if (!steps.length) {
        steps = [this.cloneBuildingParameter('defaultstep')];
        this.$set(this.displays.params.wizard, 'steps', steps);
      }

      steps.forEach((step, index) => {
        const oldFields = Array.isArray(step.fields) ? step.fields : [];
        const currentItems = Array.isArray(step.items) && step.items.length ? step.items : oldFields;
        this.$set(step, 'key', step.key || `step-${index + 1}`);
        this.$set(step, 'title', step.title || `Étape ${index + 1}`);
        this.$set(step, 'description', step.description || '');
        this.$set(step, 'items', currentItems.map((item) => this.normalizeItem(item)));
        if (Object.prototype.hasOwnProperty.call(step, 'fields')) {
          this.$delete(step, 'fields');
        }
      });

      this.syncFields();
    },

    addStep() {
      const step = this.cloneBuildingParameter('defaultstep');
      const number = this.wizard.steps.length + 1;
      step.key = `step-${number}`;
      step.title = `Étape ${number}`;
      this.wizard.steps.push(step);
    },

    removeStep(index) {
      if (this.wizard.steps.length === 1) return;
      this.wizard.steps.splice(index, 1);
      this.syncFields();
    },

    addItem(stepIndex, kind) {
      const templateNames = {
        content: 'defaultcontent',
        field: 'defaultfield',
        virtual: 'defaultvirtualfield',
        queryList: 'defaultqueryblock',
        promoCode: 'defaultpromoblock'
      };
      const item = this.cloneBuildingParameter(templateNames[kind]);
      item.blockId = this.createBlockId();

      if (kind === 'virtual') {
        item.key = `virtual-${Date.now()}`;
        item.title = 'Nouveau champ virtuel';
      }
      if (kind === 'queryList') {
        item.key = `selection-${Date.now()}`;
        item.title = 'Nouvelle liste';
      }
      if (kind === 'promoCode') {
        item.title = 'Code promo';
      }

      this.wizard.steps[stepIndex].items.push(item);
      this.syncFields();
    },

    removeItem(stepIndex, itemIndex) {
      this.wizard.steps[stepIndex].items.splice(itemIndex, 1);
      this.syncFields();
    },

    updateQueryReturnFields(item, value) {
      this.$set(item.return, 'fields', Array.isArray(value) ? value : []);
    },

    syncFields() {
      const fields = [];
      this.wizard.steps.forEach((step) => {
        step.items.forEach((item) => {
          const key = item.kind === 'field' ? item.field : item.key;
          const canInsert = (['field', 'queryList'].includes(item.kind) && item.usage === 'insert')
            || item.kind === 'promoCode';
          if (canInsert && key && !fields.includes(key)) {
            fields.push(key);
          }
        });
      });
      this.$set(this.displays.params, 'champs', fields);
    },

    onTableFieldChange(item) {
      const field = this.allFields.find((candidate) => candidate.cle === item.field);
      if (field && !item.title) {
        item.title = field.displays?.name || field.field || field.cle;
      }
      item.renderAs = this.fieldHasOptions(item.field) ? 'select' : 'auto';
      this.syncFields();
    },

    fieldHasOptions(fieldKey) {
      const field = this.allFields.find((candidate) => candidate.cle === fieldKey);
      return Boolean(field && Array.isArray(field.liste) && field.liste.length);
    },

    virtualUsesOptions(item) {
      return ['select', 'radio', 'checkboxes'].includes(item.inputType);
    },

    updateVirtualOptions(item, options) {
      this.$set(item, 'options', Array.isArray(options) ? options : []);
    },

    updateFixedFields(item, values) {
      this.$set(this.displays.params, 'defaults', Array.isArray(values) ? values : []);
    },

    updateCondition(item, key, conditions) {
      this.$set(item.conditions, key, Array.isArray(conditions) ? conditions : []);
    },

    itemTitle(item) {
      if (item.title) return item.title;
      if (item.kind === 'field' && item.field) {
        const field = this.fieldOptions.find((option) => option.value === item.field);
        return field ? field.text : item.field;
      }
      return 'Élément sans titre';
    },

    itemTypeLabel(kind) {
      const labels = {
        content: 'Texte',
        field: 'Champ de la table',
        virtual: 'Champ virtuel',
        queryList: 'Liste issue d’une requête',
        promoCode: 'Code promo'
      };
      return labels[kind] || kind;
    }
  },

  created() {
    if (!Array.isArray(this.displays.params.defaults)) {
      this.$set(this.displays.params, 'defaults', []);
    }
    this.normalizeLegacyGeneral();
    this.normalizeQueries();
    this.normalizeSteps();
  },

  mounted() {
    this.getFields();
  }
};
</script>

<style scoped>
.step-handle,
.item-handle {
  cursor: grab;
}

.publicform-add-buttons {
  gap: 8px;
}

.publicform-items {
  min-height: 48px;
}

.publicform-ghost {
  opacity: 0.45;
}
</style>
